import React from 'react';

import {getProcessTaskCore, ProcedureTaskCoreWithVersion} from 'sweet-taskinfo';

import {FullHTMLLink} from 'app/components/general/FullHTMLLink';
import {gt} from 'app/utils/i18n';

import type {AnyTaskStepType, TaskInstanceSerializedType} from 'sweet-types';

interface Props {
  taskCore: ProcedureTaskCoreWithVersion;
  isGuest?: boolean;
}

export function ProcessNavButtons({
  taskCore,
  isGuest = false,
}: Props): JSX.Element | null {
  const {task} = taskCore;

  // if it's not a process-task, bail out rendering nothing
  if (task.template.assigned_object.content_type !== 'proceduregroup') {
    return null;
  }

  const processTaskCore = getProcessTaskCore(task);

  // if the task has no version (i.e. was deleted) returns nothing
  if (processTaskCore.hasVersion !== true) {
    return null;
  }

  // activeNodes are steps available to be checked off
  // it will be a single one on sequenced processes and all unchecked on non-squenced
  const [prevNode, nextNode] = processTaskCore.state.getPrevAndNextSteps(
    taskCore.procedureHashid
  );
  return (
    <div className="d-flex border-top mt-3 pt-3 justify-content-between">
      {prevNode != null && prevNode.body != null ? (
        <FullHTMLLink
          className={BTN_CLASSES}
          html_url={getLinkToStep(
            prevNode.body as AnyTaskStepType,
            task,
            isGuest
          )}
        >
          <i className="fa fa-chevron-left mr-1" />
          {gt('Previous in Process')}
        </FullHTMLLink>
      ) : null}

      <FullHTMLLink className={BTN_CLASSES} html_url={task.html_url}>
        {gt('Back to Process')}
      </FullHTMLLink>

      {nextNode != null && nextNode.body != null ? (
        <FullHTMLLink
          className={BTN_CLASSES}
          html_url={getLinkToStep(
            nextNode.body as AnyTaskStepType,
            task,
            isGuest
          )}
        >
          {gt('Next in Process')}
          <i className="fa fa-chevron-right ml-1" />
        </FullHTMLLink>
      ) : null}
    </div>
  );
}
const BTN_CLASSES = 'small';

function getLinkToStep(
  step: AnyTaskStepType,
  task: TaskInstanceSerializedType,
  isGuest: boolean
): string {
  // procedures have their own HTML URL
  if (step.type === 'processprocedure' && !step.deleted) {
    if (isGuest) {
      // we reformat steps html_url for guests, see reformatGuestTaskURLs
      return step.html_url;
    }
    const processHashid = task.template.assigned_object.hashid;

    // decisions, ending-steps and deleted procedures will use the process task page
    return `${step.html_url}task/${task.id}/process/${processHashid}/`;
  } else {
    // with an anchor to them as HTML URL
    return `${task.html_url}#${step.id}`;
  }
}
